import { useState, useEffect, useCallback } from 'react';
import {
  UsersIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  KeyIcon,
  CheckCircleIcon,
  XCircleIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  ClockIcon,
  ArrowPathIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { usePermissions } from '../../hooks/usePermissions';
import {
  SystemUser,
  UserFormData,
  UserActivity,
  UserRole,
  ROLE_LABELS,
  ROLE_COLORS,
  ASSIGNABLE_ROLES,
} from '../../types/user';
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  activateUser,
  deactivateUser,
  resetUserPassword,
  fetchUserActivity,
  UserSearchParams,
} from '../../services/userAdminService';

const emptyForm: UserFormData = {
  full_name: '',
  email: '',
  role: 'CNS_OFFICER',
  organization: '',
  password: '',
};

export default function UserManagementPage() {
  const { hasPermission } = usePermissions();
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SystemUser | null>(null);
  const [formData, setFormData] = useState<UserFormData>({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  // Detail panel
  const [activityLog, setActivityLog] = useState<UserActivity[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  // --- Data loading ---
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: UserSearchParams = { limit: 100 };
      if (searchTerm) params.search = searchTerm;
      if (roleFilter) params.role = roleFilter;
      if (statusFilter === 'active') params.is_active = true;
      if (statusFilter === 'inactive') params.is_active = false;
      const result = await fetchUsers(params);
      setUsers(Array.isArray(result.data) ? result.data : []);
      setTotal(result.total ?? 0);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des utilisateurs');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, roleFilter, statusFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Clear success message after 3s
  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  // Derived stats
  const activeCount = users.filter(u => u.is_active).length;
  const inactiveCount = users.filter(u => !u.is_active).length;
  const adminCount = users.filter(u => (u.role as string) === 'CNS_ADMIN' || (u.role as string) === 'CNS_ADMINISTRATOR' || (u.role as string) === 'ANR_ADMIN' || (u.role as string) === 'ONT_ADMIN').length;

  // --- Handlers ---
  const handleCreateUser = async () => {
    setSaving(true);
    setActionError(null);
    try {
      await createUser(formData);
      setShowCreateModal(false);
      setFormData({ ...emptyForm });
      setSuccessMsg('Utilisateur créé avec succès. Un mot de passe temporaire a été généré.');
      await loadUsers();
    } catch (err: any) {
      setActionError(err.message || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setActionError(null);
    try {
      await updateUser(selectedUser.id, formData);
      setShowEditModal(false);
      setSelectedUser(null);
      setFormData({ ...emptyForm });
      setSuccessMsg('Utilisateur mis à jour avec succès');
      await loadUsers();
    } catch (err: any) {
      setActionError(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (user: SystemUser) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur "${user.full_name}" ?`)) return;
    setActionError(null);
    try {
      await deleteUser(user.id);
      setSuccessMsg('Utilisateur supprimé avec succès');
      await loadUsers();
    } catch (err: any) {
      setActionError(err.message || 'Erreur lors de la suppression');
    }
  };

  const handleToggleStatus = async (user: SystemUser) => {
    const action = user.is_active ? 'désactiver' : 'activer';
    if (!window.confirm(`Êtes-vous sûr de vouloir ${action} l'utilisateur "${user.full_name}" ?`)) return;
    setActionError(null);
    try {
      if (user.is_active) {
        await deactivateUser(user.id);
      } else {
        await activateUser(user.id);
      }
      setSuccessMsg(`Utilisateur ${user.is_active ? 'désactivé' : 'activé'} avec succès`);
      await loadUsers();
    } catch (err: any) {
      setActionError(err.message || 'Erreur');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setActionError(null);
    try {
      const result = await resetUserPassword(selectedUser.id);
      setTempPassword(result.temporary_password || null);
      setSuccessMsg('Mot de passe réinitialisé avec succès');
      await loadUsers();
    } catch (err: any) {
      setActionError(err.message || 'Erreur lors de la réinitialisation');
    } finally {
      setSaving(false);
    }
  };

  const handleViewDetail = async (user: SystemUser) => {
    setSelectedUser(user);
    setShowDetailPanel(true);
    setActivityLoading(true);
    setActivityLog([]);
    try {
      const logs = await fetchUserActivity(user.id, 20);
      setActivityLog(Array.isArray(logs) ? logs : []);
    } catch {
      setActivityLog([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const openEditModal = (user: SystemUser) => {
    setSelectedUser(user);
    setFormData({
      full_name: user.full_name,
      email: user.email,
      role: user.role,
      organization: user.organization,
    });
    setActionError(null);
    setShowEditModal(true);
  };

  const openResetModal = (user: SystemUser) => {
    setSelectedUser(user);
    setTempPassword(null);
    setActionError(null);
    setShowResetModal(true);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
    setStatusFilter('');
  };

  const hasActiveFilters = searchTerm || roleFilter || statusFilter;

  // --- Helpers ---
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Jamais';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleBadge = (role: string) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[role] || 'bg-gray-100 text-gray-800'}`}>
      <ShieldCheckIcon className="w-3 h-3 mr-1" />
      {ROLE_LABELS[role] || role}
    </span>
  );

  const getStatusBadge = (isActive: boolean) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="w-3 h-3 mr-1" />
          Actif
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <XCircleIcon className="w-3 h-3 mr-1" />
        Inactif
      </span>
    );
  };

  // --- Form modal renderer ---
  const renderFormModal = (title: string, onSubmit: () => void, onClose: () => void, isCreate: boolean) => (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true" role="dialog">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
          {actionError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{actionError}</div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nom complet *</label>
              <input type="text" value={formData.full_name}
                onChange={e => setFormData({ ...formData, full_name: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email *</label>
              <input type="email" value={formData.email} disabled={!isCreate}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100" />
            </div>
            {isCreate && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Mot de passe *</label>
                <input type="password" value={formData.password || ''}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Min. 8 caractères"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700">Rôle *</label>
              <select value={formData.role} disabled={!isCreate}
                onChange={e => setFormData({ ...formData, role: e.target.value as UserRole })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100">
                {ASSIGNABLE_ROLES.map(role => (
                  <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Organisation *</label>
              <input type="text" value={formData.organization}
                onChange={e => setFormData({ ...formData, organization: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
              Annuler
            </button>
            <button onClick={onSubmit}
              disabled={saving || !formData.full_name || !formData.email || !formData.organization || (isCreate && (!formData.password || formData.password.length < 8))}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // --- Loading skeleton ---
  if (loading && users.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-4 gap-6 mb-6">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>)}
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-16 bg-gray-200 rounded mb-3"></div>)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <UsersIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestion des Utilisateurs</h1>
              <p className="text-gray-600 mt-1">Gérer les utilisateurs système et leurs rôles</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={loadUsers} className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" title="Rafraîchir">
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {hasPermission('USERS_CREATE') && (
              <button onClick={() => { setFormData({ ...emptyForm }); setActionError(null); setShowCreateModal(true); }}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
                <PlusIcon className="w-4 h-4 mr-2" />
                Nouvel Utilisateur
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Success / Error banners */}
      {successMsg && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
          <p className="text-sm text-green-700">{successMsg}</p>
        </div>
      )}
      {(error || actionError) && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{error || actionError}</p>
          <button onClick={() => { setError(null); setActionError(null); }} className="text-red-400 hover:text-red-600">
            <XCircleIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <UsersIcon className="h-8 w-8 text-blue-600 flex-shrink-0" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Utilisateurs</p>
              <p className="text-2xl font-bold text-gray-900">{total || users.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-600 flex-shrink-0" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Actifs</p>
              <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <XCircleIcon className="h-8 w-8 text-red-600 flex-shrink-0" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Inactifs</p>
              <p className="text-2xl font-bold text-gray-900">{inactiveCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <ShieldCheckIcon className="h-8 w-8 text-purple-600 flex-shrink-0" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Administrateurs</p>
              <p className="text-2xl font-bold text-gray-900">{adminCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative md:col-span-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input type="text" placeholder="Rechercher par nom, email..." value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Tous les rôles</option>
              {ASSIGNABLE_ROLES.map(role => (
                <option key={role} value={role}>{ROLE_LABELS[role]}</option>
              ))}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
            <div className="flex items-center space-x-2">
              <FunnelIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-600">{users.length} résultat(s)</span>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-xs text-blue-600 hover:text-blue-800 underline">
                  Effacer filtres
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Users table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateur</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rôle</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organisation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dernière connexion</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                    <div className="text-xs text-gray-500 flex items-center mt-0.5">
                      <EnvelopeIcon className="w-3 h-3 mr-1" />{user.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{getRoleBadge(user.role)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.organization}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <ClockIcon className="w-4 h-4 mr-1 text-gray-400" />
                      {formatDate(user.last_login)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(user.is_active)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button onClick={() => handleViewDetail(user)} className="text-blue-600 hover:text-blue-900" title="Voir détails">
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      {hasPermission('USERS_EDIT') && (
                        <button onClick={() => openEditModal(user)} className="text-indigo-600 hover:text-indigo-900" title="Modifier">
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      )}
                      {hasPermission('USERS_RESET_PASSWORD') && (
                        <button onClick={() => openResetModal(user)} className="text-yellow-600 hover:text-yellow-900" title="Réinitialiser mot de passe">
                          <KeyIcon className="w-4 h-4" />
                        </button>
                      )}
                      {hasPermission('USERS_TOGGLE_STATUS') && (
                        <button onClick={() => handleToggleStatus(user)}
                          className={user.is_active ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}
                          title={user.is_active ? 'Désactiver' : 'Activer'}>
                          {user.is_active ? <XCircleIcon className="w-4 h-4" /> : <CheckCircleIcon className="w-4 h-4" />}
                        </button>
                      )}
                      {hasPermission('USERS_DELETE') && (
                        <button onClick={() => handleDeleteUser(user)} className="text-red-600 hover:text-red-900" title="Supprimer">
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {users.length === 0 && !loading && (
          <div className="text-center py-12">
            <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Aucun utilisateur trouvé</p>
            {error && (
              <button onClick={loadUsers} className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
                <ArrowPathIcon className="w-4 h-4 mr-2" />Réessayer
              </button>
            )}
          </div>
        )}
      </div>

      {/* User Detail Slide-over Panel */}
      {showDetailPanel && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-hidden" aria-modal="true" role="dialog">
          <div className="absolute inset-0 bg-gray-500 bg-opacity-50" onClick={() => { setShowDetailPanel(false); setSelectedUser(null); }}></div>
          <div className="absolute inset-y-0 right-0 max-w-lg w-full bg-white shadow-xl overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">{selectedUser.full_name}</h2>
                <button onClick={() => { setShowDetailPanel(false); setSelectedUser(null); }} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* User info */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center text-sm text-gray-600">
                  <EnvelopeIcon className="w-4 h-4 mr-2 text-gray-400" />
                  {selectedUser.email}
                </div>
                <div className="flex items-center space-x-3">
                  {getRoleBadge(selectedUser.role)}
                  {getStatusBadge(selectedUser.is_active)}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Organisation:</span> {selectedUser.organization}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Dernière connexion:</span> {formatDate(selectedUser.last_login)}
                </div>
                <div className="text-xs text-gray-400">
                  Créé le {formatDate(selectedUser.created_at)}
                </div>
              </div>

              {/* Quick actions */}
              <div className="flex space-x-2 mb-6">
                {hasPermission('USERS_EDIT') && (
                  <button onClick={() => { setShowDetailPanel(false); openEditModal(selectedUser); }}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-100 rounded-md hover:bg-indigo-200">
                    <PencilIcon className="w-4 h-4 mr-1" /> Modifier
                  </button>
                )}
                {hasPermission('USERS_RESET_PASSWORD') && (
                  <button onClick={() => { setShowDetailPanel(false); openResetModal(selectedUser); }}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-yellow-700 bg-yellow-100 rounded-md hover:bg-yellow-200">
                    <KeyIcon className="w-4 h-4 mr-1" /> Réinitialiser MDP
                  </button>
                )}
                {hasPermission('USERS_TOGGLE_STATUS') && (
                  <button onClick={() => { setShowDetailPanel(false); handleToggleStatus(selectedUser); }}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md ${
                      selectedUser.is_active
                        ? 'text-orange-700 bg-orange-100 hover:bg-orange-200'
                        : 'text-green-700 bg-green-100 hover:bg-green-200'
                    }`}>
                    {selectedUser.is_active ? <XCircleIcon className="w-4 h-4 mr-1" /> : <CheckCircleIcon className="w-4 h-4 mr-1" />}
                    {selectedUser.is_active ? 'Désactiver' : 'Activer'}
                  </button>
                )}
              </div>

              {/* Activity history */}
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ClockIcon className="w-5 h-5 mr-2 text-blue-600" />
                Historique d'activité
              </h3>
              {activityLoading ? (
                <div className="animate-pulse space-y-3">
                  {[1, 2, 3].map(i => <div key={i} className="h-12 bg-gray-100 rounded"></div>)}
                </div>
              ) : activityLog.length > 0 ? (
                <div className="space-y-3">
                  {activityLog.map((log, idx) => (
                    <div key={log.id || idx} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                          log.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {log.action}
                        </span>
                        <span className="text-xs text-gray-400">{formatDate(log.timestamp)}</span>
                      </div>
                      <div className="mt-1 text-sm text-gray-600">
                        {log.resource}{log.resource_id ? ` #${log.resource_id}` : ''}
                      </div>
                      {log.ip_address && (
                        <div className="mt-1 text-xs text-gray-400">IP: {log.ip_address}</div>
                      )}
                      {log.error_message && (
                        <div className="mt-1 text-xs text-red-500">{log.error_message}</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Aucune activité enregistrée</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showResetModal && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true" role="dialog">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => { setShowResetModal(false); setTempPassword(null); }}></div>
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Réinitialiser le mot de passe — {selectedUser.full_name}
              </h3>
              {actionError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{actionError}</div>
              )}
              {tempPassword ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700 mb-2">Mot de passe réinitialisé avec succès.</p>
                    <p className="text-sm text-gray-600">Nouveau mot de passe temporaire :</p>
                    <div className="mt-2 p-2 bg-white border border-gray-300 rounded font-mono text-sm select-all">
                      {tempPassword}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      Communiquez ce mot de passe à l'utilisateur de manière sécurisée. Il devra le changer à sa prochaine connexion.
                    </p>
                  </div>
                  <div className="flex justify-end">
                    <button onClick={() => { setShowResetModal(false); setTempPassword(null); setSelectedUser(null); }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
                      Fermer
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Un nouveau mot de passe temporaire sera généré pour <strong>{selectedUser.email}</strong>.
                    L'utilisateur devra le changer à sa prochaine connexion.
                  </p>
                  <div className="flex justify-end space-x-3">
                    <button onClick={() => { setShowResetModal(false); setTempPassword(null); }}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
                      Annuler
                    </button>
                    <button onClick={handleResetPassword} disabled={saving}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-md text-sm hover:bg-yellow-700 disabled:opacity-50">
                      {saving ? 'Réinitialisation...' : 'Réinitialiser'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreateModal && renderFormModal(
        'Nouvel Utilisateur',
        handleCreateUser,
        () => { setShowCreateModal(false); setActionError(null); },
        true
      )}

      {/* Edit modal */}
      {showEditModal && renderFormModal(
        `Modifier: ${selectedUser?.full_name}`,
        handleEditUser,
        () => { setShowEditModal(false); setSelectedUser(null); setActionError(null); },
        false
      )}
    </div>
  );
}
